// Full notifications page — filterable list with bulk actions.
//
// Reached via the bell dropdown's "See all" link. Filter chips narrow by read
// state and type (All / Unread / Replies / Likes / Mentions). Rows share the
// teal/emerald brand styling used by the bell dropdown; unread rows get a
// subtle teal tint. Clicking a row marks it read and deep-links to the post.
import React, { useState, useEffect, useCallback } from 'react';
import { Bell, User, Check, Trash2 } from 'lucide-react';
import {
  fetchList,
  markRead,
  markAllRead,
  del as deleteNotification,
  timeAgo,
  notificationMessage,
} from '../lib/notifications';

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'unread', label: 'Unread' },
  { id: 'reply', label: 'Replies' },
  { id: 'like', label: 'Likes' },
  { id: 'mention', label: 'Mentions' },
];

const NotificationsPage = ({ onOpenNotification }) => {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const { notifications } = await fetchList({ limit: 200 });
    setItems(notifications);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Client-side filtering keeps the chips instant; the list is already capped
  // at 200 by the fetch above.
  const visible = items.filter((n) => {
    if (filter === 'all') return true;
    if (filter === 'unread') return !n.read_at;
    return n.type === filter;
  });

  const unreadCount = items.filter((n) => !n.read_at).length;

  const handleRowClick = async (n) => {
    if (!n.read_at) {
      await markRead(n.id);
      setItems(prev => prev.map(x => (x.id === n.id ? { ...x, read_at: new Date().toISOString() } : x)));
    }
    if (onOpenNotification) onOpenNotification(n);
  };

  const handleMarkAll = async () => {
    await markAllRead();
    setItems(prev => prev.map(x => ({ ...x, read_at: x.read_at || new Date().toISOString() })));
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    await deleteNotification(id);
    setItems(prev => prev.filter(x => x.id !== id));
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-bold text-teal-800 flex items-center gap-2">
          <Bell className="h-5 w-5" /> Notifications
          {unreadCount > 0 && (
            <span className="text-xs font-medium text-white bg-teal-600 rounded-full px-2 py-0.5">{unreadCount}</span>
          )}
        </h1>
        <button
          onClick={handleMarkAll}
          disabled={unreadCount === 0}
          className="inline-flex items-center gap-1 text-xs font-medium text-teal-600 hover:text-teal-800 disabled:text-gray-300"
          data-testid="notifications-page-mark-all"
        >
          <Check className="h-3.5 w-3.5" /> Mark all as read
        </button>
      </div>

      {/* Filter chips */}
      <div className="flex flex-wrap gap-2 mb-4" data-testid="notification-filters">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
              filter === f.id
                ? 'bg-teal-600 text-white'
                : 'bg-teal-50 text-teal-700 hover:bg-teal-100'
            }`}
            data-testid={`notification-filter-${f.id}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="bg-white border border-teal-100 rounded-lg overflow-hidden">
        {loading ? (
          <div className="px-4 py-10 text-center text-sm text-gray-400">Loading…</div>
        ) : visible.length === 0 ? (
          <div className="px-4 py-10 text-center text-sm text-gray-400" data-testid="notifications-page-empty">
            Nothing here yet.
          </div>
        ) : (
          visible.map((n) => (
            <div
              key={n.id}
              onClick={() => handleRowClick(n)}
              className={`flex items-start gap-3 px-4 py-3.5 border-b border-gray-50 cursor-pointer hover:bg-teal-50/60 transition-colors ${
                n.read_at ? 'bg-white' : 'bg-teal-50/40'
              }`}
              data-testid={`notifications-page-row-${n.id}`}
            >
              <span className="flex-shrink-0 mt-0.5 h-8 w-8 rounded-full bg-teal-100 flex items-center justify-center">
                <User className="h-4 w-4 text-teal-700" />
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-700 leading-snug">{notificationMessage(n)}</p>
                <p className="text-xs text-gray-400 mt-0.5">{timeAgo(n.created_at)}</p>
              </div>
              {!n.read_at && <span className="flex-shrink-0 mt-2 h-2 w-2 rounded-full bg-teal-500" />}
              <button
                onClick={(e) => handleDelete(e, n.id)}
                className="flex-shrink-0 p-1 text-gray-300 hover:text-red-500 transition-colors"
                aria-label="Delete notification"
                data-testid={`notification-delete-${n.id}`}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default NotificationsPage;
